import { useCaptionHistory } from "@/pages/Posts/PostEditor/PostEditor/components/Caption/useCaptionHistory";
import TextArea, { TextAreaRef } from "antd/es/input/TextArea";
import classNames from "classnames";
import PremiumFeatureModal from "components/GlobalModals/PremiumFeatureModal";
import useAppContext from "config/AppContext/useAppContext";
import useUserPermissions from "config/UserPermissionsContext/useUserPermissions";
import { SkinTones } from "emoji-picker-react";
import { UseStateReturn } from "fixtures/globalConstants";
import { isRTLLanguage } from "fixtures/languages";
import { DEFAULT_EMOJI_SKIN_TONE } from "fixtures/localStorageKeys";
import useUpdatePostActions from "pages/Posts/utils/useUpdatePostActions";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocalStorage } from "react-use";
import { useAppSelector } from "store";
import { currentBusinessGetter } from "store/user/userSlice";
import {
  CAPTION_MODIFY_MENU,
  CaptionModifyMenuKey,
} from "../../postEditorConstants";
import CaptionActionBar from "./CaptionActionBar";
import CaptionWatermark from "./CaptionWatermark";

const PostCaptionEditor = ({
  captionState: [captionFromProps, setCaptionFromProps],
}: {
  captionState: UseStateReturn<string>;
}) => {
  const {
    themeNameState: [theme],
  } = useAppContext();
  const { loading, getModifiedCaption } = useUpdatePostActions();

  const { hasPermission } = useUserPermissions();
  const canEditCaption = hasPermission("caption_write");

  const business = useAppSelector(currentBusinessGetter);
  const {
    can_use_ai_refine: canAiRefine = true,
    post_caption_watermark: captionWatermark = false,
  } = business.owner.subscription?.restrictions ?? {};

  const [showModal, setShowModal] = useState<
    "custom-ai-refine" | "premium-feature" | null
  >(null);

  const [emojiSkinTone, setEmojiSkinTone] = useLocalStorage<SkinTones>(
    DEFAULT_EMOJI_SKIN_TONE,
    SkinTones.NEUTRAL
  );
  const textAreaRef = useRef<TextAreaRef>(null);
  const {
    caption,
    handleCaptionChange,
    handleUndo,
    handleRedo,
    insertEmoji: insertEmojiFromHook,
    resetHistory,
    canUndo,
    canRedo,
  } = useCaptionHistory(captionFromProps);

  useEffect(() => {
    if (captionFromProps !== caption) {
      resetHistory(captionFromProps);
    }
  }, [captionFromProps]);

  useEffect(() => {
    if (caption !== captionFromProps) {
      setCaptionFromProps(caption);
    }
  }, [caption]);

  const updateCaption = async (
    modifyInstruction: string,
    currentCaption: string
  ) => {
    const newCaptionResult = await getModifiedCaption({
      modifyInstruction,
      caption: currentCaption,
    });
    if (newCaptionResult) {
      const newCaption = newCaptionResult as string;
      handleCaptionChange(newCaption, false);
    }
  };

  const handleCaptionModify = async (
    key: CaptionModifyMenuKey,
    instructions?: string
  ) => {
    if (!canAiRefine) {
      setShowModal("premium-feature");
      return;
    }

    const captionForModification = caption;

    if (key === "custom" && instructions) {
      await updateCaption(instructions, captionForModification);
    } else {
      const menuItem = CAPTION_MODIFY_MENU[key];
      if (menuItem && "prompt" in menuItem && menuItem.prompt) {
        await updateCaption(
          instructions || menuItem.prompt,
          captionForModification
        );
      } else {
        console.error(`Caption modifier ${String(key)} does not have a prompt`);
      }
    }
  };

  const insertEmoji = (emoji: string) => {
    const textArea = textAreaRef.current?.resizableTextArea?.textArea;
    const cursorPosition = textArea?.selectionStart ?? null;
    insertEmojiFromHook(emoji, cursorPosition);

    setTimeout(() => {
      textArea?.focus();
      if (cursorPosition !== null && textArea) {
        const newCursorPos = cursorPosition + emoji.length;
        textArea.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  const commonModalProps = {
    open: true,
    onClose: () => setShowModal(null),
    onCancel: () => setShowModal(null),
  };

  const isRTL = useMemo(() => isRTLLanguage(caption), [caption]);

  return (
    <>
      <TextArea
        ref={textAreaRef}
        className={classNames("bg-antd-colorBgLayout !overflow-y-auto", {
          "rounded-b-none": captionWatermark,
          "text-right": isRTL,
        })}
        id="post-caption"
        placeholder="Write your caption here"
        rows={15}
        readOnly={!canEditCaption}
        disabled={Boolean(loading)}
        value={caption}
        onChange={({ target }) => handleCaptionChange(target.value)}
        variant="borderless"
      />
      {captionWatermark && <CaptionWatermark watermark={captionWatermark} />}
      {canEditCaption && (
        <CaptionActionBar
          caption={caption}
          loading={loading}
          canAiRefine={canAiRefine}
          handleCaptionModify={handleCaptionModify}
          insertEmoji={insertEmoji}
          theme={theme === "dark" ? "dark" : "light"}
          emojiSkinTone={emojiSkinTone || SkinTones.NEUTRAL}
          setEmojiSkinTone={setEmojiSkinTone}
          handleUndo={handleUndo}
          handleRedo={handleRedo}
          canUndo={canUndo}
          canRedo={canRedo}
        />
      )}

      {showModal === "premium-feature" && (
        <PremiumFeatureModal
          {...commonModalProps}
          title="AI Refine"
          content="Unlock AI Refine to automatically refine your captions."
        />
      )}
    </>
  );
};

export default PostCaptionEditor;
